import React, { useState, useEffect, useCallback } from 'react';
import '../../styles/PayoutModal.css';
import { API_BASE_URL } from '../../config';
import dayjs from 'dayjs';

function PayoutAdminModal({ isOpen, onClose, brandId, userId, authKey, onSuccess }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [payoutRequests, setPayoutRequests] = useState([]);
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [actionDetails, setActionDetails] = useState({
    utr: '',
    notes: '',
    action: null // 'approve' or 'reject'
  });

  const fetchPayoutRequests = useCallback(async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_BASE_URL}/brands/ledger/payout/get`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          brand_id: brandId,
          user_id: userId,
          auth_key: authKey
        })
      });
      
      const data = await response.json();
      if (data.status === 200) {
        setPayoutRequests(data.data);
      } else {
        throw new Error(data.message || 'Failed to fetch payout requests');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [brandId, userId, authKey]);

  useEffect(() => {
    if (isOpen) {
      fetchPayoutRequests();
    }
  }, [isOpen, fetchPayoutRequests]);

  const handlePayoutAction = async () => {
    if (!selectedPayout || !actionDetails.action) return;

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/brands/ledger/payout/update`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          payout_id: selectedPayout.payout_id,
          brand_id: brandId,
          user_id: userId,
          auth_key: authKey,
          action: actionDetails.action,
          utr: actionDetails.utr,
          notes: actionDetails.notes
        })
      });

      const data = await response.json();
      
      if (data.status === 200) {
        fetchPayoutRequests();
        setSelectedPayout(null);
        setActionDetails({ utr: '', notes: '', action: null });
        if (onSuccess) {
          onSuccess();
        }
      } else {
        throw new Error(data.message || 'Failed to update payout request');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return dayjs(dateString).format('MMM DD, YYYY HH:mm:ss');
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{padding: '0px'}}>
        <div className="modal-header">
          Manage Payout Requests
          <button className="close-button" onClick={onClose}>&times;</button>
        </div>

        {loading ? (
          <div className="modal-loading">
            <div className="loader"></div>
            <p>Loading payout requests...</p>
          </div>
        ) : error ? (
          <div className="modal-error">
            <p>{error}</p>
            <button onClick={fetchPayoutRequests}>Retry</button>
          </div>
        ) : (
          <div className="modal-body">
            <div className="payout-requests-section">
              <h3>Payout Requests</h3>
              {payoutRequests.length > 0 ? (
                <div className="payout-requests-list">
                  {payoutRequests.map((request) => (
                    <div 
                      key={request.payout_id} 
                      className={`payout-request-item ${selectedPayout?.payout_id === request.payout_id ? 'selected' : ''}`}
                      onClick={() => setSelectedPayout(request)}
                    >
                      <div className="payout-info">
                        <div className="payout-amount">
                          {formatAmount(request.amount)}
                        </div>
                        <div className="payout-status">
                          <span className={`status-badge ${request.status}`}>
                            {request.status}
                          </span>
                        </div>
                      </div>
                      <div className="payout-details">
                        <div className="payout-date">
                          Requested on: {formatDate(request.created_at)}
                        </div>
                        <div className="payout-id">
                          ID: {request.payout_id}
                        </div>
                        {request.utr && (
                          <div className="payout-utr">
                            UTR: {request.utr}
                          </div>
                        )}
                        {request.notes && (
                          <div className="payout-notes">
                            Notes: {request.notes}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="no-requests">No payout requests found</p>
              )}
            </div>

            {selectedPayout && selectedPayout.status === 'created' && (
              <div className="payout-action-section">
                <h4>Update Payout Request</h4>
                <div className="payout-action-form">
                  <div className="form-group">
                    <label>UTR Number</label>
                    <input
                      type="text"
                      value={actionDetails.utr}
                      onChange={(e) => setActionDetails(prev => ({ ...prev, utr: e.target.value }))}
                      placeholder="Enter UTR number"
                    />
                  </div>
                  <div className="form-group">
                    <label>Notes</label>
                    <textarea
                      value={actionDetails.notes}
                      onChange={(e) => setActionDetails(prev => ({ ...prev, notes: e.target.value }))}
                      placeholder="Add any additional notes"
                    />
                  </div>
                  <div className="action-buttons">
                    <button
                      className="approve-button"
                      onClick={() => {
                        setActionDetails(prev => ({ ...prev, action: 'approve' }));
                        handlePayoutAction();
                      }}
                      disabled={loading || !actionDetails.utr.trim()}
                    >
                      Approve Payout
                    </button>
                    <button
                      className="reject-button"
                      onClick={() => {
                        setActionDetails(prev => ({ ...prev, action: 'reject' }));
                        handlePayoutAction();
                      }}
                      disabled={loading || !actionDetails.notes.trim()}
                    >
                      Reject Payout
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default PayoutAdminModal; 